import classnames from 'classnames/bind';
import {
  Chart as ChartJS,
  ArcElement,
  Legend,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

import { DataWidget } from 'types/dashboard_widget';

import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { hslaToString } from 'dashboards/services/color';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useChartColors } from 'dashboards/hooks/useChartColors';
import { useLeaderboard } from 'dashboards/hooks/useLeaderboard';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/Icon';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

ChartJS.register(ArcElement, Legend, Title, Tooltip);

type PieChartProps = { widget: DataWidget<'pie'> };

export function PieChart({ widget }: PieChartProps) {
  const { metric, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const {
    data,
    isLoading: isLoadingLeaderboard,
    error,
  } = useLeaderboard({
    params: {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      ...query,
      filters,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  const labels = data?.rows.map(([label]) => label);

  const { data: colors, isLoading: isLoadingColors } = useChartColors(labels);

  const isLoading = isLoadingLeaderboard || isLoadingColors;

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data || !colors) {
    return null;
  }

  const values = data.rows.map(([_, value]) => value);
  const total = values.reduce(
    (accumulator, currentValue) => accumulator + Number(currentValue),
    0
  );

  function formatTooltipWithPercentage({
    formattedValue,
    parsed,
  }: TooltipItem<'pie'>) {
    const percentage = ((parsed / total) * 100).toFixed(2);

    return `${formattedValue} - ${percentage}%`;
  }

  const datasets = [
    {
      data: values,
      backgroundColor: colors.map(hslaToString),
    },
  ];

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `Total: ${total}`,
        position: 'bottom' as const,
      },
      tooltip: { callbacks: { label: formatTooltipWithPercentage } },
    },
  };

  return (
    <Widget
      widget={widget}
      menuItems={{
        csv: {
          children: 'Export as CSV',
          leftSection: <Icon name="export" size="small" />,
          onClick: () =>
            downloadFile(
              widget.name,
              createCsvFile({
                headers: data.headers,
                rows: data.rows,
              })
            ),
          disabled: data.rows.length === 0,
        },
      }}
    >
      <div className={c('chart')}>
        <Pie data={{ labels, datasets }} options={options} />
      </div>
    </Widget>
  );
}
